/**
 * One-time migration of legacy GameKey strings in localStorage to the new
 * dash-cased keys that match URL slugs.
 *
 * Mapping:
 *   'wordle'        → 'word-guess'
 *   'connections'   → 'word-group'
 *   'spellingBee'   → 'word-build'   (also 'spelling-bee'   prefix variant)
 *   'strands'       → 'word-find'
 *   'miniCrossword' → 'word-cross'   (also 'mini-crossword' prefix variant)
 *
 * Idempotent: rerunning is a no-op once everything has been remapped.
 * All work is wrapped in try/catch so private-mode/quota errors won't crash boot.
 */

import { browser } from '$app/environment';

/** Legacy → new value substitutions for GameKey string values stored INSIDE JSON
 *  (object keys, array entries, etc). */
const VALUE_MAP: Record<string, string> = {
	wordle: 'word-guess',
	connections: 'word-group',
	spellingBee: 'word-build',
	strands: 'word-find',
	miniCrossword: 'word-cross'
};

/** Legacy → new localStorage KEY prefix substitutions. Covers both the
 *  camelCase forms and the dashed coop forms we previously used. */
const KEY_PREFIX_MAP: Record<string, string> = {
	'wordle-': 'word-guess-',
	'connections-': 'word-group-',
	'spellingBee-': 'word-build-',
	'spelling-bee-': 'word-build-',
	'strands-': 'word-find-',
	'miniCrossword-': 'word-cross-',
	'mini-crossword-': 'word-cross-'
};

/** Storage keys whose JSON values reference GameKey strings as keys or values. */
const KEYED_JSON_BLOBS = ['bubble-games-progress', 'bubble-games-state', 'bubble-games-completions'];

const SETTINGS_KEY = 'bubble-games-settings';
const ACTIVE_COOP_KEY = 'bubble-games-active-coop';

const FLAG_KEY = 'bubble-games-storage-migrated-v1';

function remapObjectKeys<T>(obj: T): T {
	if (!obj || typeof obj !== 'object') return obj;
	if (Array.isArray(obj)) {
		return obj.map((v) => remapObjectKeys(v)) as unknown as T;
	}
	const out: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
		const newKey = VALUE_MAP[k] ?? k;
		out[newKey] = remapObjectKeys(v);
	}
	return out as T;
}

function remapStringArray(arr: unknown): unknown {
	if (!Array.isArray(arr)) return arr;
	return arr.map((v) => (typeof v === 'string' && VALUE_MAP[v] ? VALUE_MAP[v] : v));
}

function migrateLocalStorageKeyNames(): void {
	const oldKeys: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const k = localStorage.key(i);
		if (k) oldKeys.push(k);
	}

	for (const key of oldKeys) {
		for (const [oldPrefix, newPrefix] of Object.entries(KEY_PREFIX_MAP)) {
			if (!key.startsWith(oldPrefix)) continue;
			const newKey = newPrefix + key.slice(oldPrefix.length);
			if (newKey === key) break;
			const value = localStorage.getItem(key);
			if (value === null) break;
			// Don't overwrite something already migrated.
			if (localStorage.getItem(newKey) === null) {
				localStorage.setItem(newKey, value);
			}
			localStorage.removeItem(key);
			break;
		}
	}
}

function migrateKeyedJsonBlobs(): void {
	for (const storeKey of KEYED_JSON_BLOBS) {
		const raw = localStorage.getItem(storeKey);
		if (!raw) continue;
		try {
			const parsed = JSON.parse(raw);
			const remapped = remapObjectKeys(parsed);
			const next = JSON.stringify(remapped);
			if (next !== raw) localStorage.setItem(storeKey, next);
		} catch {
			/* malformed — skip */
		}
	}
}

function migrateSettings(): void {
	const raw = localStorage.getItem(SETTINGS_KEY);
	if (!raw) return;
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return;
		let changed = false;
		if (Array.isArray(parsed.gameOrder)) {
			const before = JSON.stringify(parsed.gameOrder);
			parsed.gameOrder = remapStringArray(parsed.gameOrder);
			if (JSON.stringify(parsed.gameOrder) !== before) changed = true;
		}
		if (Array.isArray(parsed.favorites)) {
			const before = JSON.stringify(parsed.favorites);
			parsed.favorites = remapStringArray(parsed.favorites);
			if (JSON.stringify(parsed.favorites) !== before) changed = true;
		}
		if (changed) localStorage.setItem(SETTINGS_KEY, JSON.stringify(parsed));
	} catch {
		/* malformed — skip */
	}
}

function migrateActiveCoop(): void {
	// Active-coop is keyed by gameType (the dashed form). Rewrite top-level keys.
	const raw = localStorage.getItem(ACTIVE_COOP_KEY);
	if (!raw) return;
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
		const next: Record<string, unknown> = {};
		let changed = false;
		const COOP_KEY_MAP: Record<string, string> = {
			wordle: 'word-guess',
			connections: 'word-group',
			'spelling-bee': 'word-build',
			spellingBee: 'word-build',
			strands: 'word-find',
			'mini-crossword': 'word-cross',
			miniCrossword: 'word-cross'
		};
		for (const [k, v] of Object.entries(parsed)) {
			const newKey = COOP_KEY_MAP[k] ?? k;
			if (newKey !== k) changed = true;
			next[newKey] = v;
		}
		if (changed) localStorage.setItem(ACTIVE_COOP_KEY, JSON.stringify(next));
	} catch {
		/* malformed — skip */
	}
}

/**
 * Idempotently migrate any legacy GameKey strings sitting in localStorage to
 * the new dash-cased values. Safe to call multiple times. Also safe to call
 * from non-browser contexts (it no-ops).
 */
export function migrateLegacyStorageKeys(): void {
	if (!browser) return;
	try {
		if (typeof localStorage === 'undefined') return;
		// We don't gate on FLAG_KEY because the function must also run AFTER a
		// cloud restore (which can drop legacy-shaped data back into storage).
		// All steps are idempotent.
		migrateLocalStorageKeyNames();
		migrateKeyedJsonBlobs();
		migrateSettings();
		migrateActiveCoop();
		try { localStorage.setItem(FLAG_KEY, '1'); } catch { /* ignore */ }
	} catch {
		/* private mode / quota — skip */
	}
}
